"use client";

/**
 * Last-resort boundary for errors thrown inside the root layout itself
 * (which `error.tsx` cannot catch because it sits under the layout).
 *
 * Must render its own <html>/<body>. Keep deps and styling minimal — if
 * we crash here too, the user is stuck on a white screen.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  if (typeof window !== "undefined") {
    // eslint-disable-next-line no-console
    console.error("[frontier:global-error]", {
      name: error.name,
      message: error.message,
      digest: error.digest,
      stack: error.stack,
      url: window.location.href,
    });
  }
  const debug = JSON.stringify(
    {
      name: error.name,
      message: error.message,
      digest: error.digest ?? null,
      stack: error.stack ?? null,
      url: typeof window !== "undefined" ? window.location.href : "",
      timestamp: new Date().toISOString(),
    },
    null,
    2,
  );
  return (
    <html lang="en">
      <body
        style={{
          fontFamily: "ui-sans-serif, system-ui, sans-serif",
          padding: "2rem",
          maxWidth: "60rem",
          margin: "0 auto",
          color: "#111",
        }}
      >
        <h1 style={{ color: "#b91c1c", fontSize: "1.5rem", margin: 0 }}>
          Root layout crashed
        </h1>
        <p style={{ color: "#4b5563", marginTop: "0.5rem" }}>
          The application's outer shell failed to render. The error and a
          full diagnostic payload follow.
        </p>
        <pre
          style={{
            marginTop: "1rem",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            padding: "1rem",
            borderRadius: "0.375rem",
            fontFamily: "ui-monospace, monospace",
            fontSize: "0.85rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {error.name}: {error.message}
          {error.digest ? `\n\ndigest: ${error.digest}` : ""}
        </pre>
        {error.stack && (
          <pre
            style={{
              marginTop: "1rem",
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              padding: "1rem",
              borderRadius: "0.375rem",
              fontFamily: "ui-monospace, monospace",
              fontSize: "0.75rem",
              maxHeight: "20rem",
              overflow: "auto",
            }}
          >
            {error.stack}
          </pre>
        )}
        <textarea
          readOnly
          value={debug}
          style={{
            marginTop: "1rem",
            width: "100%",
            height: "12rem",
            fontFamily: "ui-monospace, monospace",
            fontSize: "0.75rem",
            padding: "0.75rem",
            border: "1px solid #e5e7eb",
            borderRadius: "0.375rem",
          }}
        />
        <div style={{ marginTop: "1rem", display: "flex", gap: "0.75rem" }}>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(debug).catch(() => {});
            }}
            style={{
              background: "#111",
              color: "#fff",
              padding: "0.5rem 1rem",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer",
            }}
          >
            Copy debug info
          </button>
          <button
            type="button"
            onClick={() => reset()}
            style={{
              background: "#fff",
              color: "#111",
              border: "1px solid #d1d5db",
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
